// Native Telegram Mini App integration.
// Safe to call outside Telegram: every access is guarded, so the app also
// runs as a plain web page (e.g. tonskiup-an7.pages.dev in a browser).

type TgCloudStorage = {
  getItem: (
    key: string,
    cb: (error: string | null, value: string | null) => void
  ) => void;
  setItem?: (
    key: string,
    value: string,
    cb?: (error: string | null, stored: boolean) => void
  ) => void;
};

type TgWebApp = {
  ready: () => void;
  expand: () => void;
  enableClosingConfirmation?: () => void;
  disableClosingConfirmation?: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  disableVerticalSwipes?: () => void;
  isVersionAtLeast?: (version: string) => boolean;
  onEvent?: (event: string, cb: () => void) => void;
  themeParams?: Record<string, string>;
  colorScheme?: "light" | "dark";
  version?: string;
  CloudStorage?: TgCloudStorage;
  HapticFeedback?: {
    impactOccurred?: (style: "light" | "medium" | "heavy") => void;
    notificationOccurred?: (type: "error" | "success" | "warning") => void;
  };
};

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

function getWebApp(): TgWebApp | undefined {
  return window?.Telegram?.WebApp;
}

/** True when we are actually running inside the Telegram client. */
export function inTelegram(): boolean {
  const tg = getWebApp();
  return Boolean(tg && tg.version && tg.version !== "0.0");
}

function applyThemeColors(tg: TgWebApp) {
  const bg = tg.themeParams?.bg_color;
  // Match the Telegram chrome to the app background so there is no seam
  // between the native header and our content.
  try {
    tg.setHeaderColor?.(bg ? "bg_color" : "secondary_bg_color");
    if (bg) tg.setBackgroundColor?.(bg);
  } catch {
    /* older clients: setHeaderColor may reject non-preset values */
  }
}

/** Call once at startup. Idempotent. */
export function initTelegram() {
  const tg = getWebApp();
  if (!tg) return;
  try {
    tg.ready();
    tg.expand();
    // Let the whole page scroll without the swipe-down-to-close gesture
    // hijacking it mid-scroll (no-op on clients that lack it).
    tg.disableVerticalSwipes?.();
    applyThemeColors(tg);
    // Keep colors correct if the user flips their Telegram theme while open.
    tg.onEvent?.("themeChanged", () => applyThemeColors(tg));
  } catch (e) {
    console.warn("Telegram init skipped:", e);
  }
}

/** A short haptic tap; silently ignored outside Telegram. */
export function haptic(style: "light" | "medium" | "heavy" = "light") {
  try {
    getWebApp()?.HapticFeedback?.impactOccurred?.(style);
  } catch {
    /* ignore */
  }
}

/** Success/error/warning feedback tied to a transaction outcome. */
export function hapticNotify(type: "error" | "success" | "warning") {
  try {
    getWebApp()?.HapticFeedback?.notificationOccurred?.(type);
  } catch {
    /* ignore */
  }
}

/**
 * Read a key from Telegram CloudStorage, safely.
 * CloudStorage.getItem throws `WebAppMethodUnsupported` on older clients
 * (< 6.9) — calling it unguarded from an effect crashes the whole React
 * tree. This never throws; it just reports null when unavailable.
 */
export function cloudStorageGet(
  key: string,
  cb: (value: string | null) => void
) {
  const tg = getWebApp();
  // CloudStorage needs Bot API 6.9+. Calling it on older clients logs a
  // spurious SDK error and throws, so gate on the version first.
  if (!tg?.CloudStorage || tg.isVersionAtLeast?.("6.9") === false) {
    cb(null);
    return;
  }
  try {
    tg.CloudStorage.getItem(key, (error, value) =>
      cb(error ? null : value ?? null)
    );
  } catch {
    cb(null);
  }
}

/**
 * Guard against accidentally closing the mini app mid-transaction.
 * Enable while a chain transaction is in flight, disable when it settles.
 */
export function setClosingConfirmation(enabled: boolean) {
  const tg = getWebApp();
  if (!tg) return;
  try {
    if (enabled) tg.enableClosingConfirmation?.();
    else tg.disableClosingConfirmation?.();
  } catch {
    /* ignore */
  }
}
